import {
  AICriterionOutcomeSourceKind,
  AuditCriteriaValueCell,
  auditCriteriaValueCellIndex,
  auditCriteriaValueFlags,
  AuditCriterionValueAIStatus,
  AuditCriterionValueSource,
} from 'models/auditCriteriaGeneric';
import {
  AuditCriteriaPeriod,
  AuditCriteriaValue,
  AuditCriteriaValueColumn,
  AuditCriteriaValueOutcome,
  AuditCriteriaValues,
} from 'models/auditCriteriaValues';
import {
  AuditCriteriaValueColumnDTO,
  AuditCriteriaValueOutcomeDTO,
  AuditCriteriaValuesQueryDTO,
  AuditCriteriaValuesResultDTO,
} from 'models/dto/auditCriteriaGenericDTO';
import { addDateDays, fromApiDateTimeOptional, fromApiDateTimeRequired, toApiDateTimeOptional } from 'utils/datetime';

export function mapToAuditCriteriaValuesQueryDTO(
  entityType: number,
  entityIds: number[] | undefined,
  period: AuditCriteriaPeriod,
  lineageKeys?: string[],
): AuditCriteriaValuesQueryDTO {
  const output = new AuditCriteriaValuesQueryDTO();
  output.entityType = entityType;
  output.entityIds = entityIds && entityIds.length > 0 ? entityIds : null;
  output.period = period.kind;
  output.from = toApiDateTimeOptional(period.from);
  // the picked end day is part of the period; the server reads to as exclusive
  output.to = toApiDateTimeOptional(period.to ? addDateDays(period.to, 1) : undefined);
  output.lineageKeys = lineageKeys && lineageKeys.length > 0 ? lineageKeys : null;

  return output;
}

export function mapFromAuditCriteriaValueColumnDTO(dto: AuditCriteriaValueColumnDTO): AuditCriteriaValueColumn {
  const output = new AuditCriteriaValueColumn();
  output.lineageKey = dto.lineageKey ?? '';
  output.requirement = dto.requirement ?? '';
  output.columnName = dto.columnName ?? '';
  output.mode = dto.mode ?? '';
  output.auditCriteriaSetId = dto.auditCriteriaSetId ?? 0;
  output.subType = dto.subType ?? undefined;
  output.specific = dto.specific ?? false;
  output.sortOrder = dto.sortOrder ?? 0;

  return output;
}

// The compact cell [resultNumber, resultDate, taskId, flags]: the flag bits as models/auditCriteriaGeneric.ts names them.
export function mapFromAuditCriteriaValueCell(cell: AuditCriteriaValueCell): AuditCriteriaValue {
  const output = new AuditCriteriaValue();
  const flags = cell[auditCriteriaValueCellIndex.flags] ?? 0;
  const resultNumber = cell[auditCriteriaValueCellIndex.resultNumber];
  const taskId = cell[auditCriteriaValueCellIndex.taskId];
  const source = (flags >> auditCriteriaValueFlags.sourceShift) & auditCriteriaValueFlags.sourceMask;

  output.resultNumber = resultNumber === null || resultNumber === undefined ? undefined : Number(resultNumber);
  output.resultDate = fromApiDateTimeRequired(cell[auditCriteriaValueCellIndex.resultDate]);
  output.taskId = taskId === null || taskId === undefined ? undefined : taskId;
  output.hasComment = (flags & auditCriteriaValueFlags.hasComment) !== 0;
  output.hasEvidence = (flags & auditCriteriaValueFlags.hasEvidence) !== 0;
  output.source = source === 0 ? undefined : (source as AuditCriterionValueSource);
  output.aiStatus = ((flags >> auditCriteriaValueFlags.aiStatusShift) &
    auditCriteriaValueFlags.aiStatusMask) as AuditCriterionValueAIStatus;

  return output;
}

export function mapFromAuditCriteriaValueOutcomeDTO(dto: AuditCriteriaValueOutcomeDTO): AuditCriteriaValueOutcome {
  const output = new AuditCriteriaValueOutcome();
  output.outcomeId = dto.outcomeId ?? 0;
  output.proposedResult = dto.proposedResult ?? output.proposedResult;
  output.sourceKind = (dto.sourceKind || output.sourceKind) as AICriterionOutcomeSourceKind;
  output.sourceRef = dto.sourceRef ?? '';
  output.sourceName = dto.sourceName ?? '';
  output.created = fromApiDateTimeRequired(dto.created);

  return output;
}

export function mapFromAuditCriteriaValuesResultDTO(dto: AuditCriteriaValuesResultDTO): AuditCriteriaValues {
  const output = new AuditCriteriaValues();
  output.columns = (dto.columns ?? []).map(mapFromAuditCriteriaValueColumnDTO);
  output.from = fromApiDateTimeOptional(dto.from);
  output.to = fromApiDateTimeOptional(dto.to);

  for (const [entityId, cells] of Object.entries(dto.values ?? {})) {
    const byLineage = new Map<string, AuditCriteriaValue>();
    for (const [lineageKey, cell] of Object.entries(cells ?? {})) {
      byLineage.set(lineageKey, mapFromAuditCriteriaValueCell(cell));
    }

    output.values.set(Number(entityId), byLineage);
  }

  for (const [entityId, outcomes] of Object.entries(dto.outcomes ?? {})) {
    const byLineage = new Map<string, AuditCriteriaValueOutcome>();
    for (const [lineageKey, outcome] of Object.entries(outcomes ?? {})) {
      byLineage.set(lineageKey, mapFromAuditCriteriaValueOutcomeDTO(outcome));
    }

    output.outcomes.set(Number(entityId), byLineage);
  }

  return output;
}
